/** Your personal agents, on your account page (an agent's row on the members list: `personalEntry.tsx`). */
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { Membership, PageMember, PersonalAgent } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { agentLabel } from "../../lib/agentPresets.ts";
import { usePersonalAgents } from "../../lib/data.ts";
import { freeAgents, mine, takenAgents } from "../../lib/personalAgents.ts";
import { useAction, useResource } from "../../lib/resource.ts";
import { atLeast } from "../../lib/roles.ts";
import { me } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";
import { Button } from "../../ui/Button.tsx";
import { ConfirmButton } from "../../ui/Dialog.tsx";
import { Field, Form, Select } from "../../ui/form.tsx";
import { Section } from "../../ui/misc.tsx";
import { RowList } from "../../ui/RowList.tsx";

/** Removing a personal agent, after asking. */
function Remove({ org, link, onChange }: { org: string; link: PersonalAgent; onChange: () => void }) {
  const [remove, busy] = useAction(async () => {
    await api.del(`/orgs/${org}/personal-agents/${link.agent}`);
    toast(t("personal.removed"));
    onChange();
  });
  return (
    <ConfirmButton
      size="sm"
      variant="danger"
      label={t("personal.remove")}
      title={t("personal.removeTitle")}
      message={t("personal.removeMessage", { agent: link.agent_name, person: link.person_name })}
      confirmLabel={t("personal.remove")}
      busy={busy}
      onConfirm={remove}
    />
  );
}

/** One org's part of your personal agents: those working for you, and making a free agent yours. */
function OrgAgents({ org, many }: { org: Membership; many: boolean }) {
  const links = usePersonalAgents(org.org);
  const members = useResource(`members:${org.org}`, (signal) =>
    api.get<PageMember>(`/orgs/${org.org}/members`, undefined, signal).then((p) => p.data),
  );
  const self = me.value?.id ?? "";
  const reload = () => {
    links.reload();
    members.reload();
  };
  // Only agents you set up are yours to make yours here (admins make any agent anyone's on the members page).
  const yours = members.data?.filter((m) => m.set_up_by === self) ?? [];
  const free = links.data ? freeAgents(yours, links.data) : [];
  // Yours that work for someone else (an admin moved them): whose, and that they have to let go of it first.
  const taken = links.data && !atLeast(org.role, "admin") ? takenAgents(yours, links.data, self) : [];
  const [agent, setAgent] = useState("");
  const chosen = free.find((m) => m.id === agent) ?? free[0];
  const [make, busy] = useAction(async () => {
    if (!chosen) {
      return;
    }
    await api.post(`/orgs/${org.org}/personal-agents`, { agent: chosen.id });
    toast(t("personal.made", { agent: chosen.name, person: me.value?.name ?? "" }));
    setAgent("");
    reload();
  });
  if (!links.data) {
    return null;
  }
  const rows = mine(links.data, self);
  return (
    <div class="mb-4 flex flex-col gap-3">
      {many ? <h3 class="text-sm font-medium">{org.name}</h3> : null}
      {rows.length === 0 ? (
        <p class="text-sm text-zinc-500">{t("personal.none")}</p>
      ) : (
        <RowList
          rows={rows.map((l) => ({
            key: l.agent,
            title: agentLabel(l.agent_name, l.person_name),
            detail:
              l.granted_by === self ? null : (
                <span class="text-xs text-zinc-500">{t("personal.grantedBy", { name: l.granted_by_name ?? "" })}</span>
              ),
            aside: <Remove org={org.org} link={l} onChange={reload} />,
          }))}
        />
      )}
      {chosen || taken.length > 0 ? (
        <Form class="flex flex-wrap items-end gap-2" onSubmit={make}>
          <Field label={t("personal.agent")} hint={t("personal.hint")}>
            <Select
              value={chosen?.id ?? ""}
              onValue={setAgent}
              options={[
                ...free.map((m) => ({ value: m.id, label: m.name })),
                ...taken.map(({ agent: a, person }) => ({
                  value: a.id,
                  label: t("personal.taken", { agent: a.name, person }),
                  disabled: true,
                })),
              ]}
            />
          </Field>
          <Button submit={true} busy={busy} disabled={!chosen}>
            {t("personal.makeMine")}
          </Button>
        </Form>
      ) : null}
    </div>
  );
}

/** Your personal agents, in each of your organizations (your account page). */
export function PersonalAgents() {
  const orgs = me.value?.orgs ?? [];
  if (orgs.length === 0 || me.value?.kind !== "human") {
    return null;
  }
  return (
    <Section title={t("personal.section")}>
      <p class="mb-3 text-sm text-zinc-600">{t("personal.sectionHint")}</p>
      {orgs.map((o) => (
        <OrgAgents key={o.org} org={o} many={orgs.length > 1} />
      ))}
    </Section>
  );
}
